import startsWith from 'lodash/fp/startsWith';
import urlJoin from 'url-join';

export type RequestHeaders = typeof requestHeaders;

type InjectedSettings = typeof injectedAppSettings;

interface IAppSettings {
  /**
   * Base URL for the WS Web Socket service.
   */
  readonly wsUrl: string;

  /**
   * Base URL for the ES2 WebApi.
   */
  readonly apiUrl: string;

  readonly exportsUrl: string;

  /**
   * Base URL for the Auth WebApp.
   */
  readonly authUrl: string;

  /**
   * Base URL for the Cumulus WebApi.
   */
  readonly cumulusApiUrl: string;

  /**
   * Base URL for the ES2 public API.
   */
  readonly publicApiUrl: string;

  /**
   * Base URL for GraphQL
   */
  readonly graphQLUrl: string;

  /**
   * URL to the reporting server.
   */
  readonly reportsServerUrl: string;

  /**
   * API key used for Google Maps.
   */
  readonly googleMapsApiKey: string;

  /**
   * Product ID for [Beamer](https://www.getbeamer.com/docs/#parameters)
   */
  readonly beamerProductId: string;

  /**
   * The build/release number for this version.
   */
  readonly releaseNumber: string;

  /**
   * The measurement id sent to google analytics for tracking
   */
  readonly googleAnalyticsMeasurementId: string;

  /**
   * The baseUrl tells the react router how much of the URL to "ignore" when
   * figuring out the location.
   *
   * When developing using the webpack dev server, our base path is '/' - but
   * when running through IIS (both on a local machine or when deployed) the
   * base path shifts to '/auth/'.
   */
  readonly baseUrl: string;
  readonly isDevelopment: boolean;
  readonly environment: string;

  /**
   * Request headers required for authorization
   */
  readonly requestHeaders: RequestHeaders;

  buildApiUrl(...segments: string[]): string;
  buildPublicApiUrl(...segments: string[]): string;

  readonly isProduction: boolean;

  readonly frontendVersion: string;

  /**
   * Whether project is enabled
   */
  readonly hasProject: boolean;
}

let injectedAppSettings = {
  get omnisUrl(): string {
    return '#{OmnisHostUri}';
  },
  get es1Url(): string {
    return '#{MvcHostUri}';
  },
  get wsUrl(): string {
    return '#{WSAppHostUri}';
  },
  get apiUrl(): string {
    return '#{WebApiAppHostUri}';
  },
  get exportsUrl(): string {
    return '#{CumulusExporterAppHostUri}';
  },
  get authUrl(): string {
    return '#{appSettings:LoginUri}';
  },
  get graphQLUrl(): string {
    return '#{GraphQLApiUri}';
  },
  get cumulusApiUrl(): string {
    return '#{CumulusAppHostUri}';
  },
  get reportsServerUrl(): string {
    return '#{ReportsAppHostUri}';
  },
  get publicApiUrl(): string {
    return '#{ES2VersionedApiUri}';
  },
  get environment() {
    return import.meta.env.MODE;
  },
  get googleMapsApiKey(): string {
    return '#{GoogleMapsApiKey.DomainRestricted}';
  },
  get mapboxApiKey(): string {
    return '#{MapboxApiKey.DomainRestricted}';
  },
  get bingMapsApiKey(): string {
    return '#{BingMapsApiKey.DomainRestricted}';
  },
  get beamerProductId(): string {
    return '#{BeamerProductId.DomainRestricted}';
  },
  get releaseNumber(): string {
    return '#{Octopus.Release.Number}';
  },
  get googleAnalyticsMeasurementId(): string {
    return '#{GoogleAnalyticsMeasurementId}';
  },
  get icpCertificate(): string {
    return '#{China-IcpRegistrations}';
  },
  get internetSecurityNumberText(): string {
    return '#{China-InternetSecurityNumberText}';
  },
  get internetSecurityNumber(): string {
    return '#{China-InternetSecurityNumber}';
  },
  get apiKey(): string {
    return '';
  },
  get isProduction() {
    return !!'#{OmnisAppHost}'.match(/^omnis.envirosuite.[a-z]+/);
  },
  get isCommunity(): boolean {
    return preservePlaceholder('#{IsCommunity}').toLowerCase() === 'true';
  },

  get frontendVersion(): string {
    // There are a number of rules set up in Cloudfront to handle file caching depending
    // on the type of request. This value is currently used for browser cache busting
    // and I want to make sure any requests that include it aren't misinterpreted as
    // a particular file type, so making the value alphanumeric just in case.
    return alphanumeric('#{OmnisFrontendVersion}');
  },

  get debug(): boolean {
    return preservePlaceholder('#{Debug}').toLowerCase() === 'true';
  },

  get hasProject(): boolean {
    return preservePlaceholder('#{HasProject}').toLowerCase() === 'true';
  },
};

function alphanumeric(value: string) {
  return value.replace(/\W/g, '');
}

// Cater for development where Octopus doesn't run, hence no substitution
if (startsWith('#{', injectedAppSettings.cumulusApiUrl)) {
  const PUBLIC_API_HOST = import.meta.env.VITE_PUBLIC_API_HOST || `http://localhost:60000/api`;
  const API_HOST = import.meta.env.VITE_API_HOST || `http://localhost:61000/api`;
  const CUMULUS_API_HOST = import.meta.env.VITE_CUMULUS_API_HOST || `https://localhost:7123`;
  const AUTH_HOST = import.meta.env.VITE_AUTH_HOST || `http://localhost:62000`;
  const REPORTING_HOST = import.meta.env.VITE_REPORTING_HOST || `https://localhost:5001/reports`;
  const GRAPHQL_HOST = import.meta.env.VITE_GRAPHQL_HOST || `http://localhost:60000/api/graphql`;
  const WS_HOST = import.meta.env.VITE_WS_HOST || `wss://localhost:5263`;
  const EXPORTS_HOST = import.meta.env.VITE_EXPORTS_HOST || `https://localhost:5002`;

  // Used for the returnUrl
  const OMNIS_HOST = import.meta.env.VITE_OMNIS_HOST || `http://localhost:3000`;
  // Only used for ES1 reports
  const ES1_HOST = import.meta.env.VITE_ES1_HOST || `http://localhost:60000`;

  // Check if we have stuff in environment variables
  injectedAppSettings = {
    get omnisUrl(): string {
      return OMNIS_HOST;
    },
    get es1Url(): string {
      return ES1_HOST;
    },
    get wsUrl(): string {
      return WS_HOST;
    },
    get apiUrl(): string {
      return API_HOST;
    },
    get exportsUrl(): string {
      return EXPORTS_HOST;
    },
    get authUrl(): string {
      return AUTH_HOST;
    },
    get graphQLUrl(): string {
      return GRAPHQL_HOST;
    },
    get cumulusApiUrl(): string {
      return CUMULUS_API_HOST;
    },
    get reportsServerUrl(): string {
      return REPORTING_HOST;
    },
    get publicApiUrl(): string {
      return PUBLIC_API_HOST;
    },
    get environment() {
      return import.meta.env.MODE;
    },
    get googleMapsApiKey(): string {
      return 'AIzaSyAZsoqQGsDFID-waQbSinVR3NQkq9j-msQ';
    },
    get mapboxApiKey(): string {
      return (
        import.meta.env.VITE_MAPBOX_ACCESS_TOKEN ||
        'pk.eyJ1IjoiZW52aXJvc3VpdGUtYnJpc2JhbmUiLCJhIjoiY2sybzY1OWNpMDd6ZDNkczd4Z2Z2cWxxZyJ9.-wuqJp_YE_zFr4JjPsZHAg'
      );
    },
    get bingMapsApiKey(): string {
      return import.meta.env.VITE_BING_MAPS_API_KEY;
    },
    get beamerProductId(): string {
      return import.meta.env.VITE_BEAMER_PRODUCT_ID;
    },
    get releaseNumber(): string {
      return 'Development';
    },
    get googleAnalyticsMeasurementId(): string {
      return 'UA-8575676-11';
    },
    get icpCertificate(): string {
      return '';
    },
    get internetSecurityNumber(): string {
      return '';
    },
    get internetSecurityNumberText(): string {
      return '';
    },
    get apiKey(): string {
      return import.meta.env.VITE_API_KEY ?? '';
    },
    get isProduction() {
      return import.meta.env.PROD;
    },
    get isCommunity(): boolean {
      return import.meta.env.VITE_IS_COMMUNITY;
    },
    get frontendVersion() {
      return crypto.getRandomValues(new Uint32Array(1))[0]?.toString() ?? '';
    },
    get debug() {
      return true;
    },
    get hasProject() {
      return true;
    },
  };
} else if (!startsWith('http', injectedAppSettings.cumulusApiUrl)) {
  throw new Error(
    `Cumulus API URL is not set correctly. Expected a full URL, got: ${injectedAppSettings.cumulusApiUrl}`
  );
}

// Prevent bundler from optimizing away Octopus placeholders (e.g. '#{HasProject}')
function preservePlaceholder(value: string): string {
  return value;
}

const requestHeaders = {
  companyAlias: 'PEL-Company-Alias' as const,
  facilityAlias: 'PEL-Facility-Alias' as const,
};

const baseUrl = '/';

const createUrlBuilder =
  (urlBase: string) =>
  (...segments: string[]) =>
    urlJoin(urlBase, ...segments);

export const AppSettings: IAppSettings & InjectedSettings = {
  ...injectedAppSettings,

  get baseUrl(): string {
    return baseUrl;
  },
  get isDevelopment(): boolean {
    return import.meta.env.MODE === 'development';
  },
  get requestHeaders() {
    return requestHeaders;
  },

  buildApiUrl: createUrlBuilder(injectedAppSettings.apiUrl),
  buildPublicApiUrl: createUrlBuilder(injectedAppSettings.publicApiUrl),
};
